/**
 * Guest-photo ingestion, in the browser.
 *
 * The port of the server's `services/images.py`, with the same promise: a photo
 * is never stored as received. It is decoded, turned upright, fitted inside
 * `LIMITS.photoMaxEdgePx`, flattened onto white and re-encoded as JPEG. A canvas
 * export carries no metadata, so EXIF — GPS included, which wedding photos
 * routinely hold — cannot ride along into storage or into a backup file.
 */

import { LIMITS, MAX_UPLOAD_BYTES } from '../config'
import { ApiError } from '../types'

export interface ProcessedPhoto {
  blob: Blob
  width: number
  height: number
}

const JPEG_QUALITY = 0.86

/** Refuse what the server refused, before spending any time decoding it. */
export function checkUpload(file: Blob): void {
  if (file.size === 0) throw new ApiError(422, 'invalid_image', 'The file is empty.')
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new ApiError(413, 'upload_too_large', `Photos are limited to ${LIMITS.maxUploadMb} MB.`)
  }
}

/** The size an image takes when fitted inside a `maxEdge` square; never enlarged. */
export function fitInside(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const scale = Math.min(1, maxEdge / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export async function processPhoto(
  file: Blob,
  maxEdge: number = LIMITS.photoMaxEdgePx,
): Promise<ProcessedPhoto> {
  checkUpload(file)
  const image = await decode(file)
  try {
    const size = fitInside(image.width, image.height, maxEdge)
    const canvas = drawScaled(image, size.width, size.height)
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    )
    if (!blob) throw new ApiError(422, 'invalid_image', 'The photo could not be re-encoded.')
    return { blob, width: size.width, height: size.height }
  } finally {
    image.close()
  }
}

/**
 * A photo's pixels, upright and resized to `sizeFor(width, height)` — for
 * analysis (the floor-plan import), never for storage. Same decoding and
 * downscaling as `processPhoto`.
 */
export async function readPixels(
  file: Blob,
  sizeFor: (width: number, height: number) => { width: number; height: number },
): Promise<ImageData> {
  checkUpload(file)
  const image = await decode(file)
  try {
    const size = sizeFor(image.width, image.height)
    const canvas = drawScaled(image, size.width, size.height)
    const context = canvas.getContext('2d')
    if (!context) throw new ApiError(422, 'invalid_image', 'No 2D canvas available.')
    return context.getImageData(0, 0, size.width, size.height)
  } finally {
    image.close()
  }
}

/**
 * A 2×2 HEIC image (453 bytes). Decoding it is the only reliable way to ask a
 * browser whether it reads HEIC — iPhones' native format: Safari does, Chrome
 * and Firefox do not, and no API says so without trying.
 */
const TINY_HEIC =
  'AAAAHGZ0eXBoZWljAAAAAG1pZjFoZWljbWlhZgAAAXxtZXRhAAAAAAAAACFoZGxyAAAAAAAAAABwaWN0AAAAAAAAAAAAAAAAAAAAACJpbG9jAAAAAERAAAEAAQAAAAABoAABAAAAAAAAACUAAAAjaWluZgAAAAAAAQAAABVpbmZlAgAAAAABAABodmMxAAAAAA5waXRtAAAAAAABAAAA/GlwcnAAAADcaXBjbwAAAHVodmNDAQNwAAAAAAAAAAAAHvAA/P34+AAADwNgAAEAGEABDAH//wNwAAADAJAAAAMAAAMAHroCQGEAAQApQgEBA3AAAAMAkAAAAwAAAwAeoCCBBZbqrprm4CGgwIAAAAyAAAADAIRiAAEABkQBwXPBiQAAABNjb2xybmNseAABAA0ABoAAAAAUaXNwZQAAAAAAAABAAAAAQAAAAChjbGFwAAAAAgAAAAEAAAACAAAAAf///8IAAAAC////wgAAAAIAAAAQcGl4aQAAAAADCAgIAAAAGGlwbWEAAAAAAAAAAQABBYECAwWEAAAALW1kYXQAAAAhKAGvBBITDOD46cJoPup67y5MvJ+0qyvjSZ93sRiSqf+g'

let heicSupport: Promise<boolean> | null = null

/** Can this browser decode HEIC photos? Asked once, answered from a tiny sample. */
export function canDecodeHeic(): Promise<boolean> {
  heicSupport ??= (async () => {
    try {
      const bytes = Uint8Array.from(atob(TINY_HEIC), (char) => char.charCodeAt(0))
      const image = await decode(new Blob([bytes], { type: 'image/heic' }))
      image.close()
      return image.width > 0
    } catch {
      return false
    }
  })()
  return heicSupport
}

/** Does this file claim to be HEIC/HEIF — by type, or failing that by name? */
export function isHeic(file: Blob): boolean {
  const name = 'name' in file && typeof file.name === 'string' ? file.name : ''
  return /^image\/hei[cf](-sequence)?$/i.test(file.type) || /\.hei[cf]$/i.test(name)
}

interface Decoded {
  source: CanvasImageSource
  width: number
  height: number
  close: () => void
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      // `from-image` applies the camera's EXIF orientation, as the server's
      // `ImageOps.exif_transpose` did, so a portrait never lands on its side.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
    } catch {
      // Fall through: <img> decodes some formats createImageBitmap does not,
      // HEIC in Safari among them.
    }
  }

  const url = URL.createObjectURL(file)
  const img = new Image()
  img.decoding = 'async'
  img.src = url
  try {
    await img.decode()
  } catch {
    URL.revokeObjectURL(url)
    // An iPhone photo in a browser that cannot read HEIC is a different problem
    // from a broken file, with a different way out.
    if (isHeic(file)) {
      throw new ApiError(415, 'heic_unsupported', 'This browser cannot decode HEIC images.')
    }
    throw new ApiError(422, 'invalid_image', 'That file could not be read as an image.')
  }
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    close: () => URL.revokeObjectURL(url),
  }
}

/**
 * Downscale in halving steps, then once more to the exact size. A single big
 * bilinear step samples only a few source pixels per output pixel and turns a
 * face into shimmer; halving averages them in.
 */
function drawScaled(image: Decoded, width: number, height: number): HTMLCanvasElement {
  let source: CanvasImageSource = image.source
  let sourceWidth = image.width
  let sourceHeight = image.height

  while (sourceWidth / 2 >= width * 1.5 && sourceHeight / 2 >= height * 1.5) {
    const step = document.createElement('canvas')
    step.width = Math.round(sourceWidth / 2)
    step.height = Math.round(sourceHeight / 2)
    const context = step.getContext('2d')
    if (!context) break
    context.imageSmoothingQuality = 'high'
    context.drawImage(source, 0, 0, step.width, step.height)
    source = step
    sourceWidth = step.width
    sourceHeight = step.height
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new ApiError(422, 'invalid_image', 'No 2D canvas available.')
  // JPEG has no alpha: flatten transparent PNGs onto white, as the server did,
  // rather than letting the encoder turn transparency black.
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, width, height)
  return canvas
}
